import FilterCheckbox from "@/components/FilterCheckbox";
import FilterSelect from "@/components/FilterSelect";
import SearchFocusButton from "@/components/SearchFocusButton";
import { SORT, SORT_ITEM_MAP, SORT_LIST } from "@/constants/sort";
import { useMaterialSourceFilter } from "@/utils/hooks/useMaterialSourceFilter";
import splitTopicUrlnameAndFilterId from "@/utils/splitTopicUrlnameAndFilterId";
import { useUpdateEffect } from "ahooks";
import { useRouter } from "next/router";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Else, If, Then } from "react-if";
import styles from "./index.module.less";
import TopicNav from "./nav";

function FilterCheckboxWithData({ data, value, onChange, ...props }: any) {
  const onValueChange = useCallback(
    (checked) => {
      onChange && onChange(checked ? data : null);
    },
    [onChange, data]
  );

  return <FilterCheckbox value={!!value} onChange={onValueChange} {...props} />;
}

export interface TopicFilterRef {
  setFieldsValue: (values: any) => void;
  getFieldsValue: () => any;
}

const TopicFilter = forwardRef<TopicFilterRef, any>(function TopicFilter(
  { topic, onChange, forceSort, onForceSortApplied, isRemix = true },
  ref
) {
  const [sort, setSort] = useState(SORT_ITEM_MAP[SORT.ALL]);
  const [filter, setFilter] = useState<Record<string, any>>({});
  const router = useRouter();
  const lastForceSortRef = useRef(null);

  const { urlname: params } = router.query;
  const { urlname, filter_id: filterId } = splitTopicUrlnameAndFilterId(params);

  // 通过 ref 暴露 setFieldsValue 和 getFieldsValue 方法，保持与原有 API 兼容
  useImperativeHandle(
    ref,
    () => ({
      setFieldsValue: (values: any) => {
        if (values.sort !== undefined) {
          setSort(values.sort);
        }
        if (values.filter !== undefined) {
          setFilter((prev) => ({ ...prev, ...values.filter }));
        }
      },
      getFieldsValue: () => ({
        sort,
        filter,
      }),
    }),
    [sort, filter]
  );

  // 当外部强制设置排序时，更新值
  useEffect(() => {
    if (forceSort && lastForceSortRef.current !== forceSort) {
      lastForceSortRef.current = forceSort;
      setSort(forceSort);
      // 通知父组件已经应用了强制排序
      if (onForceSortApplied) {
        onForceSortApplied();
      }
    }
  }, [forceSort, onForceSortApplied]);

  const groupOptions = useMemo(() => {
    return (
      topic?.contents?.map((content) => {
        return {
          label: content.title,
          img: content.img,
          value: content.filter.value,
        };
      }) || []
    );
  }, [topic?.contents]);

  const groupValue = useMemo(() => {
    if (!groupOptions.length) {
      return null;
    }

    if (filterId) {
      return (
        groupOptions.find((option) => `${option.value}` === `${filterId}`) ||
        groupOptions[0]
      );
    }

    return groupOptions[0];
  }, [filterId, groupOptions]);

  // 使用素材来源筛选器 Hook
  const { filter: materialSourceFilter } = useMaterialSourceFilter();

  const onGroupChange = useCallback(
    (value) => {
      router.push(`/topics/${urlname}-${value.value}`);
    },
    [router, urlname]
  );

  // 处理排序变化
  const onSortChange = useCallback((value) => {
    setSort(value);
  }, []);

  // 处理筛选条件变化
  const onFilterChange = useCallback((key: string, value: any) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
  }, []);

  useUpdateEffect(() => {
    if (sort && groupValue) {
      let ids = [groupValue.value];
      Object.values(filter || {}).forEach((item: any) => {
        if (item) {
          ids.push(item.value);
        }
      });
      onChange &&
        onChange({
          ids,
          sort: sort.value,
          // 这个用于埋点
          sortName: sort.label,
          filterOne: groupValue.label,
        });
    }
  }, [onChange, sort, filter, groupValue]);

  return (
    <div className={styles.container}>
      {/* 如果只有1个子主题，就不展示子场景的名字 */}
      {groupOptions?.length > 1 && (
        <TopicNav
          options={groupOptions}
          value={groupValue}
          onChange={onGroupChange}
          isRemix={isRemix}
        />
      )}

      {!isRemix && (
        <div className={styles.filter}>
          <div className={styles.left}>
            <FilterSelect
              allowClear={false}
              options={SORT_LIST}
              value={sort}
              onChange={onSortChange}
            />

            {materialSourceFilter && (
              <FilterSelect
                name={materialSourceFilter.name}
                options={materialSourceFilter.options}
                getPopupContainer={() => document.body}
                value={filter[materialSourceFilter.key]}
                onChange={(value) =>
                  onFilterChange(materialSourceFilter.key, value)
                }
              />
            )}
          </div>
          <div className={styles.right}>
            {topic?.filters
              ?.filter((item) => item.showOutside)
              ?.map((filterItem) => (
                <If
                  key={filterItem.key}
                  condition={filterItem.children.length <= 0}
                >
                  <Then>
                    <FilterCheckboxWithData
                      className={styles.item}
                      name={filterItem.name}
                      data={filterItem}
                      iconType="img"
                      icon={filterItem.icon}
                      value={filter[filterItem.key]}
                      onChange={(value) =>
                        onFilterChange(filterItem.key, value)
                      }
                    />
                  </Then>
                  <Else>
                    <FilterSelect
                      className={styles.item}
                      iconType="img"
                      optionType={filterItem.type}
                      icon={filterItem.icon}
                      name={filterItem.name}
                      options={filterItem.children}
                      value={filter[filterItem.key]}
                      onChange={(value) =>
                        onFilterChange(filterItem.key, value)
                      }
                    />
                  </Else>
                </If>
              ))}
            <SearchFocusButton
              className={styles.button}
              searchScope="searchTopicPin"
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default TopicFilter;
